import { FC, useState, ReactElement } from "react";
import { Box, Typography, TextField } from "@mui/material";
import { css } from "@emotion/css";
import { UI_ACCENT_BLUE } from "../../constants/ui-colors";

const MAX_LENGTH = 250;
const TEXTAREA_MIN_ROWS = 3;
const TEXTAREA_MAX_ROWS = 10;

const styles = {
  noteCreateContainer: css`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
  `,
  noteCreateRow: css`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 0.5rem;
    width: 100%;
  `,
  noteCreateFooter: css`
    display: flex;
    justify-content: flex-start;
  `,
};

/** @deprecated import UI_ACCENT_BLUE from constants/ui-colors; kept for callers */
export const NOTE_ACCENT_BLUE = UI_ACCENT_BLUE;

/** Shared styles for the add / submit control next to the note textarea */
export const noteSubmitButtonSx = {
  backgroundColor: UI_ACCENT_BLUE,
  color: "#fff",
  minWidth: "2.5rem",
  width: "2.5rem",
  minHeight: "2.5rem",
  height: "2.5rem",
  padding: 0,
  borderRadius: "0.25rem",
  fontSize: "1.35rem",
  fontWeight: 500,
  lineHeight: 1,
  textTransform: "none",
  boxShadow: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  "& .MuiButton-label": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
  },
  "&:hover": {
    backgroundColor: "#1565c0",
    boxShadow: "none",
  },
} as const;

const sxStyles = {
  noteCreateTextField: {
    flex: 1,
    minWidth: 0,
    "& .MuiInputBase-input": {
      fontSize: "0.875rem",
      fontWeight: "400",
      fontFamily: "Roboto, sans-serif, Helvetica, Arial",
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: `${UI_ACCENT_BLUE} !important`,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: `${UI_ACCENT_BLUE} !important`,
        borderWidth: "1px",
      },
    },
  },
  wordCount: {
    color: "text.secondary",
    fontSize: "0.75rem",
  },
};

interface NoteCreateProps {
  noteText?: string;
  button?: ReactElement;
  onSubmit?: (text: string) => void;
}

const NoteCreate: FC<NoteCreateProps> = ({ noteText, button, onSubmit }) => {
  const [text, setText] = useState<string>(noteText || "");

  const handleTextChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newText = event.target.value;
    if (newText.length <= MAX_LENGTH) {
      setText(newText);
    }
  };

  const handleSubmit = () => {
    const trimmed = text.trim();
    if (!trimmed || !onSubmit) {
      return;
    }
    onSubmit(trimmed);
    setText("");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box className={styles.noteCreateContainer}>
      <Box className={styles.noteCreateRow}>
        <TextField
          multiline
          minRows={TEXTAREA_MIN_ROWS}
          maxRows={TEXTAREA_MAX_ROWS}
          variant="outlined"
          value={text}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          sx={sxStyles.noteCreateTextField}
        />
        {button && (
          <Box
            onClick={handleSubmit}
            sx={{ flexShrink: 0, display: "flex", alignItems: "flex-start" }}
          >
            {button}
          </Box>
        )}
      </Box>
      <Box className={styles.noteCreateFooter}>
        <Typography variant="caption" sx={sxStyles.wordCount}>
          {text.length}/{MAX_LENGTH} characters
        </Typography>
      </Box>
    </Box>
  );
};

export default NoteCreate;
